/**
 * AcoesEditor — componente shared pra editar lista de actions de automação.
 *
 * Sprint 3.1 (BUG-V2-058 paridade — 2026-05-26). Extraído do
 * `WorkflowAutomationForm` (1445 LOC monolítico) pra permitir reuso em
 * `QuestionarioAutomationForm` + `TaskTypeAutomationForm`.
 *
 * Acessa o react-hook-form via `form: UseFormReturn<TForm>` prop (não usa
 * FormProvider/context — explícito por design). Caller passa também `name`
 * indicando o caminho do array de actions no schema (geralmente 'actions').
 *
 * Suporta os 13 tipos de action canônicos do `automations-engine`:
 *
 *  1. notify_in_app          (entrega de verdade)
 *  2. send_whatsapp          (o engine aceita, mas nada é enviado ainda)
 *  3. send_email             (o engine aceita, mas nada é enviado ainda)
 *  4. create_task            (cria task no card)
 *  5. move_card              (mover entre colunas — Kanban only)
 *  6. assign_responsavel     (atribuir responsável)
 *  7. celebrar_e_notificar   (confete + notif)
 *  8. criar_card_workflow    (cross-workflow)
 *  9. setar_prioridade       (atualiza card)
 * 10. adicionar_tag          (atualiza card)
 * 11. criar_atividade_card   (cria atividade)
 * 12. agendar_calendar       (Google Calendar)
 * 13. webhook_externo        (HTTP POST/PUT)
 *
 * Filtro via prop `allowedActionTypes` (default = todas 13). Permite TaskType
 * mostrar só as 3 ações que o engine task_type_automations atualmente suporta.
 *
 * ⚠️ NÃO É WIZARD — é o conteúdo PURO de uma seção "Ações". Caller monta
 * o resto do form (trigger, conditions, identificação, save button).
 */

import { useFieldArray, type UseFormReturn, type FieldPath, type FieldValues, type ArrayPath } from 'react-hook-form';

import { Badge, Button, Input, Label, Select, Switch } from '@/components/ui';
import type { AutomationAction } from '@/lib/automations-store';
import type { KanbanColumn } from '@/lib/use-kanban-columns';

// ─── Constantes públicas (exportadas) ──────────────────────────────────

/** Lista canônica dos 13 tipos de ação (ordem visual default no select). */
export const AUTOMATION_ACTION_TYPES = [
  'notify_in_app',
  'send_whatsapp',
  'send_email',
  'create_task',
  'move_card',
  'assign_responsavel',
  'celebrar_e_notificar',
  'criar_card_workflow',
  'setar_prioridade',
  'adicionar_tag',
  'criar_atividade_card',
  'agendar_calendar',
  'webhook_externo',
] as const;

export type AutomationActionType = (typeof AUTOMATION_ACTION_TYPES)[number];

/**
 * FASE-C · regra "nada de enfeite": WhatsApp e E-mail existem no engine mas
 * NUNCA disparam de verdade. O admin não pode mais escolher essas duas ações
 * ao montar uma automação nova.
 *
 * Elas continuam no `AUTOMATION_ACTION_TYPES` (o backend, o engine e as
 * automações já salvas dependem dos tipos) — o que muda é só a oferta no
 * seletor. Se uma automação JÁ salva usa uma delas, a opção reaparece SÓ
 * naquele card, desabilitada e marcada "Em preparação", pra a tela não
 * quebrar nem apagar o dado do usuário.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const ACOES_EM_PREPARACAO: readonly AutomationActionType[] = [
  'send_whatsapp',
  'send_email',
];

// eslint-disable-next-line react-refresh/only-export-components
export const ACTION_TYPE_LABELS: Record<AutomationActionType, string> = {
  notify_in_app: 'Avisar no app',
  send_whatsapp: 'WhatsApp — em preparação',
  send_email: 'E-mail — em preparação',
  create_task: 'Criar tarefa no card',
  move_card: 'Mover o card pra outra coluna',
  assign_responsavel: 'Definir o responsável',
  celebrar_e_notificar: 'Celebrar e avisar o time (com confete)',
  criar_card_workflow: 'Abrir um card em outro fluxo',
  setar_prioridade: 'Mudar a prioridade',
  adicionar_tag: 'Marcar o card com uma tag',
  criar_atividade_card: 'Registrar uma atividade no card',
  agendar_calendar: 'Agendar no Google Calendar',
  webhook_externo: 'Avisar um sistema externo',
};

const TARGET_LABEL_NOTIFY: Record<string, string> = {
  responsavel: 'Responsável pelo card',
  criador: 'Quem criou o card',
  todos_no_card: 'Todo mundo no card',
  user_specific: 'Uma pessoa específica',
};
const TARGET_LABEL_EXTERNAL: Record<string, string> = {
  responsavel: 'Responsável pelo card',
  cliente: 'Cliente',
  lista: 'Uma lista de pessoas',
};
const TARGET_LABEL_TASK: Record<string, string> = {
  responsavel: 'Responsável pelo card',
  criador: 'Quem criou o card',
  user_specific: 'Uma pessoa específica',
  round_robin: 'Rodízio entre várias pessoas',
};
const STRATEGY_LABEL: Record<string, string> = {
  fixed: 'Sempre a mesma pessoa',
  round_robin: 'Rodízio entre várias pessoas',
  least_loaded: 'Quem tem menos negócios em aberto',
};

// ─── Default shapes por tipo (extraído do switch do Workflow form) ─────

/**
 * Retorna shape default pra uma action nova do tipo dado.
 * Usado quando user troca o tipo no dropdown (precisa reset dos campos
 * pra fields_específicos_do_novo_tipo).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function defaultActionForType(
  type: AutomationActionType,
  opts: { firstColumnSlug?: string } = {},
): AutomationAction {
  switch (type) {
    case 'notify_in_app':
      return {
        type: 'notify_in_app',
        target: 'responsavel',
        message: 'Lembrete sobre {{cliente}}',
      };
    case 'send_whatsapp':
      return {
        type: 'send_whatsapp',
        target: 'responsavel',
        message: '',
      };
    case 'send_email':
      return {
        type: 'send_email',
        target: 'responsavel',
        subject: '',
        body: '',
      };
    case 'create_task':
      return {
        type: 'create_task',
        target: 'responsavel',
        texto: 'Acompanhar {{cliente}}',
        prazoDiasUteis: 3,
      };
    case 'move_card':
      return {
        type: 'move_card',
        targetColumnSlug: opts.firstColumnSlug ?? '',
        registrarAtividade: true,
      };
    case 'assign_responsavel':
      return {
        type: 'assign_responsavel',
        strategy: 'fixed',
        userId: '',
      };
    case 'celebrar_e_notificar':
      return {
        type: 'celebrar_e_notificar',
        tituloTemplate: '{{cliente}} fechou {{valor}}!',
        mensagemTemplate: 'Negócio fechado: {{valor}}',
        scope: 'dept',
      };
    case 'criar_card_workflow':
      return {
        type: 'criar_card_workflow',
        workflowAlvoSlug: '',
        colunaAlvoSlug: null,
        tituloTemplate: '{{cliente}} — follow-up',
        descricaoTemplate: null,
        responsavelEstrategia: 'workflow_owner',
        responsavelIdFixo: null,
        responsavelTag: null,
        prioridade: null,
        copiarFotos: false,
        copiarRelato: false,
        incluirLinkOrigem: true,
      };
    case 'setar_prioridade':
      return {
        type: 'setar_prioridade',
        cardAlvo: 'pai',
        novaPrioridade: 'alta',
      };
    case 'adicionar_tag':
      return {
        type: 'adicionar_tag',
        cardAlvo: 'pai',
        tag: '',
      };
    case 'criar_atividade_card':
      return {
        type: 'criar_atividade_card',
        cardAlvo: 'pai',
        activityTypeId: '',
        descricaoTemplate: 'Automação: {{cliente}}',
      };
    case 'agendar_calendar':
      return {
        type: 'agendar_calendar',
        calendarUserEstrategia: 'workflow_owner',
        calendarUserIdFixo: null,
        calendarUserTag: null,
        tituloTemplate: '{{cliente}}',
        descricaoTemplate: null,
        startModo: 'offset_minutes',
        startOffsetMinutes: 60,
        startDataFixaIso: null,
        duracaoMinutes: 30,
        attendeesEmails: [],
      };
    case 'webhook_externo':
      return {
        type: 'webhook_externo',
        url: 'https://',
        method: 'POST',
        bodyTemplate: '{}',
        headersJson: null,
      };
  }
}

// ─── Props ─────────────────────────────────────────────────────────────

export interface AcoesEditorProps<TForm extends FieldValues> {
  /** React-Hook-Form return value do PARENT (não usa FormProvider — explícito). */
  form: UseFormReturn<TForm>;
  /** Caminho do array de actions no schema. Geralmente 'actions'. */
  name: FieldPath<TForm>;
  /** Colunas do workflow (obrigatório se action `move_card` permitida). */
  columns?: KanbanColumn[];
  /**
   * Subset permitido de action types. Default = todas as 13.
   *
   * Use pra TaskType (engine só suporta 3) ou contextos que filtram
   * intencionalmente. Default exposto via `AUTOMATION_ACTION_TYPES`.
   */
  allowedActionTypes?: readonly AutomationActionType[];
  /**
   * Mensagem opcional no rodapé (lista de tokens disponíveis, etc).
   * Default tokens canônicos do Kanban.
   */
  tokensHelpText?: React.ReactNode;
}

const DEFAULT_TOKENS_HELP = (
  <>
    Você pode usar estes atalhos no texto — o sistema troca cada um pelo valor do
    card: <code>{'{{cliente}}'}</code> <code>{'{{produto}}'}</code>{' '}
    <code>{'{{servico}}'}</code> <code>{'{{valor}}'}</code>{' '}
    <code>{'{{responsavel}}'}</code> <code>{'{{coluna_anterior}}'}</code>{' '}
    <code>{'{{coluna_atual}}'}</code> <code>{'{{dias_parado}}'}</code>
  </>
);

// ─── Component ─────────────────────────────────────────────────────────

export function AcoesEditor<TForm extends FieldValues>({
  form,
  name,
  columns = [],
  allowedActionTypes = AUTOMATION_ACTION_TYPES,
  tokensHelpText,
}: AcoesEditorProps<TForm>) {
  // Cast: name aponta pra um array de AutomationAction[] no schema do form.
  // useFieldArray + useForm têm tipos complexos quando genérico — caller
  // garante via Zod que o path aponta pro shape correto.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const formAny = form as unknown as UseFormReturn<any>;
  const { control, register, watch, setValue } = formAny;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // useFieldArray genérico — caller garante que `name` aponta pra array.
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as unknown as ArrayPath<FieldValues>,
  });

  const moveCardAllowed = allowedActionTypes.includes('move_card');
  const firstColumnSlug = columns[0]?.slug;

  // FASE-C: a oferta do seletor não inclui as ações que ainda não disparam.
  const tiposOferecidos = allowedActionTypes.filter(
    (t) => !ACOES_EM_PREPARACAO.includes(t),
  );

  return (
    <div className="min-w-0 space-y-3" data-testid="acoes-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="!mb-0">O que a automação faz</Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid="acoes-editor-add"
          onClick={() => {
            // Pega o primeiro tipo OFERECIDO como default (notify_in_app
            // normalmente é o primeiro). Garante shape válido e nunca cria
            // uma ação nova num tipo "em preparação".
            const defaultType = tiposOferecidos[0] ?? allowedActionTypes[0] ?? 'notify_in_app';
            append(defaultActionForType(defaultType, { firstColumnSlug }));
          }}
        >
          + Adicionar ação
        </Button>
      </div>

      {fields.map((field, idx) => {
        const path = `${String(name)}.${idx}`;
        const actionType = watch(`${path}.type`) as AutomationActionType;

        // Automação JÁ salva num tipo em preparação: a opção volta pra lista
        // (senão o <select> ficaria em branco e o próximo save apagaria o
        // dado) — mas desabilitada, pra não poder ser escolhida de novo.
        const emPreparacao = ACOES_EM_PREPARACAO.includes(actionType);
        const opcoesDoSeletor = emPreparacao
          ? allowedActionTypes.filter(
              (t) => !ACOES_EM_PREPARACAO.includes(t) || t === actionType,
            )
          : tiposOferecidos;

        return (
          <div
            key={field.id}
            data-testid={`acoes-editor-action-${idx}`}
            className="min-w-0 rounded-xl border border-line dark:border-line-dark p-3 space-y-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="!mb-0">Ação {idx + 1}</Label>
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-xs text-red dark:text-red-glow hover:underline"
                  data-testid={`acoes-editor-remove-${idx}`}
                >
                  Remover
                </button>
              )}
            </div>

            {emPreparacao && (
              <p
                className="flex flex-wrap items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2"
                data-testid={`acoes-editor-${idx}-em-preparacao`}
              >
                <Badge variant="warning" size="sm">
                  Em preparação
                </Badge>
                Esta ação foi configurada antes e continua salva, mas ainda não é
                enviada. Troque por outra ação quando quiser que ela funcione.
              </p>
            )}

            {/* Dropdown de tipo + campo principal (Para quem / Coluna alvo / etc) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="min-w-0">
                <Label htmlFor={`acoes-editor-${idx}-type`}>O que fazer</Label>
                <Select
                  id={`acoes-editor-${idx}-type`}
                  data-testid={`acoes-editor-${idx}-type`}
                  value={actionType}
                  onChange={(e) => {
                    const t = e.target.value as AutomationActionType;
                    setValue(path, defaultActionForType(t, { firstColumnSlug }));
                  }}
                >
                  {opcoesDoSeletor.map((t) => (
                    <option
                      key={t}
                      value={t}
                      disabled={ACOES_EM_PREPARACAO.includes(t)}
                    >
                      {ACTION_TYPE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="min-w-0">
                {actionType === 'move_card' && moveCardAllowed ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-targetcol`}>Coluna de destino</Label>
                    <Select
                      id={`acoes-editor-${idx}-targetcol`}
                      data-testid={`acoes-editor-${idx}-targetcol`}
                      {...register(`${path}.targetColumnSlug`)}
                    >
                      {columns.map((c) => (
                        <option key={c.slug} value={c.slug}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </>
                ) : actionType === 'assign_responsavel' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-strategy`}>Como escolher</Label>
                    <Select
                      id={`acoes-editor-${idx}-strategy`}
                      data-testid={`acoes-editor-${idx}-strategy`}
                      {...register(`${path}.strategy`)}
                    >
                      {Object.entries(STRATEGY_LABEL).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </Select>
                  </>
                ) : actionType === 'celebrar_e_notificar' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-scope`}>Quem vê a celebração</Label>
                    <Select
                      id={`acoes-editor-${idx}-scope`}
                      data-testid={`acoes-editor-${idx}-scope`}
                      {...register(`${path}.scope`)}
                    >
                      <option value="dept">O departamento do fluxo</option>
                      <option value="workflow">Todo mundo do fluxo</option>
                      <option value="global">A empresa inteira</option>
                    </Select>
                  </>
                ) : actionType === 'setar_prioridade' ||
                  actionType === 'adicionar_tag' ||
                  actionType === 'criar_atividade_card' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-cardalvo`}>Em qual card</Label>
                    <Select
                      id={`acoes-editor-${idx}-cardalvo`}
                      data-testid={`acoes-editor-${idx}-cardalvo`}
                      {...register(`${path}.cardAlvo`)}
                    >
                      <option value="pai">Neste card</option>
                      <option value="filho_recem_criado">No card que acabou de ser criado</option>
                    </Select>
                  </>
                ) : actionType === 'criar_card_workflow' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-wfalvo`}>Fluxo de destino</Label>
                    <Input
                      id={`acoes-editor-${idx}-wfalvo`}
                      data-testid={`acoes-editor-${idx}-wfalvo`}
                      placeholder="Ex: manutencao"
                      hint="Identificador do fluxo — veja em Configurações › Fluxos de trabalho."
                      {...register(`${path}.workflowAlvoSlug`)}
                    />
                  </>
                ) : actionType === 'agendar_calendar' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-calendaruser`}>Na agenda de quem</Label>
                    <Select
                      id={`acoes-editor-${idx}-calendaruser`}
                      data-testid={`acoes-editor-${idx}-calendaruser`}
                      {...register(`${path}.calendarUserEstrategia`)}
                    >
                      <option value="workflow_owner">Responsável pelo fluxo</option>
                      <option value="fixo">Sempre a mesma pessoa</option>
                      <option value="round_robin">Rodízio entre várias pessoas</option>
                      <option value="least_loaded">Quem tem menos negócios em aberto</option>
                      <option value="tag">Quem tiver a tag indicada</option>
                    </Select>
                  </>
                ) : actionType === 'webhook_externo' ? (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-method`}>Tipo de envio</Label>
                    <Select
                      id={`acoes-editor-${idx}-method`}
                      data-testid={`acoes-editor-${idx}-method`}
                      {...register(`${path}.method`)}
                    >
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                    </Select>
                  </>
                ) : (
                  <>
                    <Label htmlFor={`acoes-editor-${idx}-target`}>Pra quem</Label>
                    <Select
                      id={`acoes-editor-${idx}-target`}
                      data-testid={`acoes-editor-${idx}-target`}
                      {...register(`${path}.target`)}
                    >
                      {actionType === 'notify_in_app' &&
                        Object.entries(TARGET_LABEL_NOTIFY).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      {(actionType === 'send_whatsapp' || actionType === 'send_email') &&
                        Object.entries(TARGET_LABEL_EXTERNAL).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      {actionType === 'create_task' &&
                        Object.entries(TARGET_LABEL_TASK).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                    </Select>
                  </>
                )}
              </div>
            </div>

            {/* Campos específicos por tipo (mensagem, prazo, conditions, etc) */}

            {actionType === 'send_email' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-subject`}>Assunto</Label>
                  <Input
                    id={`acoes-editor-${idx}-subject`}
                    placeholder="Ex: Novidade sobre {{cliente}}"
                    {...register(`${path}.subject`)}
                  />
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-body`}>Texto do e-mail</Label>
                  <textarea
                    id={`acoes-editor-${idx}-body`}
                    rows={3}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
                    placeholder="Ex: O card de {{cliente}} entrou em {{coluna_atual}}."
                    {...register(`${path}.body`)}
                  />
                </div>
              </>
            )}

            {(actionType === 'notify_in_app' || actionType === 'send_whatsapp') && (
              <div>
                <Label htmlFor={`acoes-editor-${idx}-message`}>Mensagem</Label>
                <textarea
                  id={`acoes-editor-${idx}-message`}
                  data-testid={`acoes-editor-${idx}-message`}
                  rows={2}
                  className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
                  placeholder="Ex: {{cliente}} está parado há {{dias_parado}} dias."
                  {...register(`${path}.message`)}
                />
              </div>
            )}

            {actionType === 'create_task' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-texto`}>Texto da tarefa</Label>
                  <textarea
                    id={`acoes-editor-${idx}-texto`}
                    data-testid={`acoes-editor-${idx}-texto`}
                    rows={2}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
                    placeholder="Ex: Ligar pra {{cliente}} confirmar visita"
                    {...register(`${path}.texto`)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="min-w-0">
                    <Label htmlFor={`acoes-editor-${idx}-prazo`}>Prazo (dias úteis)</Label>
                    <Input
                      id={`acoes-editor-${idx}-prazo`}
                      data-testid={`acoes-editor-${idx}-prazo`}
                      type="number"
                      min={1}
                      max={60}
                      {...register(`${path}.prazoDiasUteis`)}
                      hint="Pula sábados, domingos e feriados nacionais."
                    />
                  </div>
                  {watch(`${path}.target`) === 'user_specific' && (
                    <div className="min-w-0">
                      <Label htmlFor={`acoes-editor-${idx}-userid`}>Quem é a pessoa</Label>
                      <Input
                        id={`acoes-editor-${idx}-userid`}
                        placeholder="Cole aqui o identificador da pessoa"
                        hint="Copie em Configurações › Pessoas e acesso › Usuários."
                        {...register(`${path}.userId`)}
                      />
                    </div>
                  )}
                </div>
                {watch(`${path}.target`) === 'round_robin' && (
                  <div>
                    <Label htmlFor={`acoes-editor-${idx}-pool`}>Pessoas do rodízio</Label>
                    <Input
                      id={`acoes-editor-${idx}-pool`}
                      placeholder="Cole os identificadores separados por vírgula"
                      defaultValue={
                        ((watch(`${path}.pool`) as string[] | undefined) ?? []).join(', ')
                      }
                      onChange={(e) => {
                        const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                        setValue(`${path}.pool`, arr, { shouldValidate: false });
                      }}
                      hint="Copie o identificador de cada pessoa em Configurações › Pessoas e acesso › Usuários. A cada disparo, a vez passa pra próxima da lista."
                    />
                  </div>
                )}
              </>
            )}

            {actionType === 'move_card' && moveCardAllowed && (
              <div>
                <Switch
                  label="Registrar a mudança de coluna no histórico do card"
                  checked={watch(`${path}.registrarAtividade`) !== false}
                  onChange={(e) =>
                    setValue(`${path}.registrarAtividade`, e.target.checked, { shouldValidate: false })
                  }
                />
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                  Mover o card pode disparar outras automações. O sistema interrompe a
                  corrente depois de 5 automações encadeadas, pra não entrar em laço.
                </p>
              </div>
            )}

            {actionType === 'assign_responsavel' && (
              <>
                {watch(`${path}.strategy`) === 'fixed' && (
                  <div>
                    <Label htmlFor={`acoes-editor-${idx}-fixeduser`}>Quem é a pessoa</Label>
                    <Input
                      id={`acoes-editor-${idx}-fixeduser`}
                      placeholder="Cole aqui o identificador da pessoa"
                      hint="Copie em Configurações › Pessoas e acesso › Usuários."
                      {...register(`${path}.userId`)}
                    />
                  </div>
                )}
                {(watch(`${path}.strategy`) === 'round_robin' ||
                  watch(`${path}.strategy`) === 'least_loaded') && (
                  <div>
                    <Label htmlFor={`acoes-editor-${idx}-assignpool`}>
                      Pessoas que entram na conta
                    </Label>
                    <Input
                      id={`acoes-editor-${idx}-assignpool`}
                      placeholder="Cole os identificadores separados por vírgula"
                      defaultValue={
                        ((watch(`${path}.pool`) as string[] | undefined) ?? []).join(', ')
                      }
                      onChange={(e) => {
                        const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                        setValue(`${path}.pool`, arr, { shouldValidate: false });
                      }}
                      hint={
                        watch(`${path}.strategy`) === 'round_robin'
                          ? 'A cada disparo a vez passa pra próxima da lista. Copie os identificadores em Configurações › Pessoas e acesso › Usuários.'
                          : 'Vai pra quem estiver com menos negócios em aberto. Copie os identificadores em Configurações › Pessoas e acesso › Usuários.'
                      }
                    />
                  </div>
                )}
              </>
            )}

            {actionType === 'celebrar_e_notificar' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-celebrar-titulo`}>
                    Título da celebração
                  </Label>
                  <Input
                    id={`acoes-editor-${idx}-celebrar-titulo`}
                    data-testid={`acoes-editor-${idx}-celebrar-titulo`}
                    placeholder="{{cliente}} fechou {{valor}}!"
                    {...register(`${path}.tituloTemplate`)}
                  />
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-celebrar-mensagem`}>
                    Mensagem do aviso
                  </Label>
                  <textarea
                    id={`acoes-editor-${idx}-celebrar-mensagem`}
                    data-testid={`acoes-editor-${idx}-celebrar-mensagem`}
                    rows={2}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
                    placeholder="Negócio fechado: {{valor}}"
                    {...register(`${path}.mensagemTemplate`)}
                  />
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                    <code>{'{{cliente}}'}</code> vira o nome do cliente.{' '}
                    <code>{'{{valor}}'}</code> já vem formatado (ex.: <em>R$ 1.250,00</em>) —
                    não escreva &quot;R$&quot; antes.
                  </p>
                </div>
              </>
            )}

            {actionType === 'criar_card_workflow' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="min-w-0">
                    <Label htmlFor={`acoes-editor-${idx}-coluna-alvo`}>
                      Coluna de destino (opcional)
                    </Label>
                    <Input
                      id={`acoes-editor-${idx}-coluna-alvo`}
                      placeholder="Ex: aberta"
                      hint="Deixe vazio pra cair na primeira coluna do fluxo."
                      onChange={(e) =>
                        setValue(`${path}.colunaAlvoSlug`, e.target.value || null, {
                          shouldValidate: false,
                        })
                      }
                      defaultValue={
                        (watch(`${path}.colunaAlvoSlug`) as string | null | undefined) ?? ''
                      }
                    />
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor={`acoes-editor-${idx}-resp-strategy`}>
                      Quem fica responsável
                    </Label>
                    <Select
                      id={`acoes-editor-${idx}-resp-strategy`}
                      {...register(`${path}.responsavelEstrategia`)}
                    >
                      <option value="workflow_owner">Responsável pelo fluxo de destino</option>
                      <option value="fixo">Sempre a mesma pessoa</option>
                      <option value="round_robin">Rodízio entre várias pessoas</option>
                      <option value="least_loaded">Quem tem menos negócios em aberto</option>
                      <option value="tag">Quem tiver a tag indicada</option>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-titulo-card`}>Título do novo card</Label>
                  <Input
                    id={`acoes-editor-${idx}-titulo-card`}
                    placeholder="{{cliente}} — follow-up"
                    {...register(`${path}.tituloTemplate`)}
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <Switch
                    label="Copiar relato"
                    checked={watch(`${path}.copiarRelato`) === true}
                    onChange={(e) =>
                      setValue(`${path}.copiarRelato`, e.target.checked, { shouldValidate: false })
                    }
                  />
                  <Switch
                    label="Copiar fotos"
                    checked={watch(`${path}.copiarFotos`) === true}
                    onChange={(e) =>
                      setValue(`${path}.copiarFotos`, e.target.checked, { shouldValidate: false })
                    }
                  />
                  <Switch
                    label="Incluir link de origem"
                    checked={watch(`${path}.incluirLinkOrigem`) === true}
                    onChange={(e) =>
                      setValue(`${path}.incluirLinkOrigem`, e.target.checked, {
                        shouldValidate: false,
                      })
                    }
                  />
                </div>
              </>
            )}

            {actionType === 'setar_prioridade' && (
              <div>
                <Label htmlFor={`acoes-editor-${idx}-nova-prioridade`}>Nova prioridade</Label>
                <Select
                  id={`acoes-editor-${idx}-nova-prioridade`}
                  {...register(`${path}.novaPrioridade`)}
                >
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </Select>
              </div>
            )}

            {actionType === 'adicionar_tag' && (
              <div>
                <Label htmlFor={`acoes-editor-${idx}-tag`}>Tag</Label>
                <Input
                  id={`acoes-editor-${idx}-tag`}
                  data-testid={`acoes-editor-${idx}-tag-valor`}
                  placeholder="Ex: urgente"
                  {...register(`${path}.tag`)}
                  hint="A tag é salva em letras minúsculas e sem espaços nas pontas."
                />
              </div>
            )}

            {actionType === 'criar_atividade_card' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-activity-type`}>Tipo de atividade</Label>
                  <Input
                    id={`acoes-editor-${idx}-activity-type`}
                    placeholder="Cole aqui o identificador do tipo"
                    {...register(`${path}.activityTypeId`)}
                    hint="Copie em Configurações › Catálogos › Tipos de atividade."
                  />
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-atividade-desc`}>Descrição</Label>
                  <textarea
                    id={`acoes-editor-${idx}-atividade-desc`}
                    rows={2}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
                    placeholder="Ex: Registro automático do card de {{cliente}}"
                    {...register(`${path}.descricaoTemplate`)}
                  />
                </div>
              </>
            )}

            {actionType === 'agendar_calendar' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-agendar-titulo`}>Título do evento</Label>
                  <Input
                    id={`acoes-editor-${idx}-agendar-titulo`}
                    placeholder="Visita {{cliente}}"
                    {...register(`${path}.tituloTemplate`)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div className="min-w-0">
                    <Label htmlFor={`acoes-editor-${idx}-start-modo`}>Quando marcar</Label>
                    <Select id={`acoes-editor-${idx}-start-modo`} {...register(`${path}.startModo`)}>
                      <option value="offset_minutes">Um tempo depois do disparo</option>
                      <option value="data_fixa">Numa data e hora fixas</option>
                    </Select>
                  </div>
                  {watch(`${path}.startModo`) === 'offset_minutes' ? (
                    <div className="min-w-0">
                      <Label htmlFor={`acoes-editor-${idx}-offset`}>Daqui a quantos minutos</Label>
                      <Input
                        id={`acoes-editor-${idx}-offset`}
                        type="number"
                        min={1}
                        {...register(`${path}.startOffsetMinutes`)}
                      />
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <Label htmlFor={`acoes-editor-${idx}-data-fixa`}>Data e hora</Label>
                      <Input
                        id={`acoes-editor-${idx}-data-fixa`}
                        placeholder="2026-06-01T09:00:00Z"
                        hint="Formato ano-mês-diaThora:minuto:segundoZ."
                        {...register(`${path}.startDataFixaIso`)}
                      />
                    </div>
                  )}
                  <div className="min-w-0">
                    <Label htmlFor={`acoes-editor-${idx}-duracao`}>Duração (minutos)</Label>
                    <Input
                      id={`acoes-editor-${idx}-duracao`}
                      type="number"
                      min={1}
                      {...register(`${path}.duracaoMinutes`)}
                    />
                  </div>
                </div>
              </>
            )}

            {actionType === 'webhook_externo' && (
              <>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-webhook-url`}>Endereço do sistema</Label>
                  <Input
                    id={`acoes-editor-${idx}-webhook-url`}
                    placeholder="https://exemplo.com/receber"
                    {...register(`${path}.url`)}
                    hint="Precisa começar com https://. Endereços internos da rede são bloqueados por segurança."
                  />
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-webhook-body`}>Conteúdo enviado</Label>
                  <textarea
                    id={`acoes-editor-${idx}-webhook-body`}
                    rows={3}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 font-mono text-xs"
                    placeholder='{"cliente": "{{cliente}}", "valor": "{{valor}}"}'
                    {...register(`${path}.bodyTemplate`)}
                  />
                </div>
                <div>
                  <Label htmlFor={`acoes-editor-${idx}-webhook-headers`}>
                    Cabeçalhos (JSON, opcional)
                  </Label>
                  <textarea
                    id={`acoes-editor-${idx}-webhook-headers`}
                    rows={2}
                    className="w-full min-w-0 rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 font-mono text-xs"
                    placeholder='{"X-Api-Key": "..."}'
                    defaultValue={(watch(`${path}.headersJson`) as string | null | undefined) ?? ''}
                    onChange={(e) =>
                      setValue(`${path}.headersJson`, e.target.value || null, {
                        shouldValidate: false,
                      })
                    }
                  />
                </div>
              </>
            )}
          </div>
        );
      })}

      <p className="text-xs text-ink-3 dark:text-ink-dark-3">
        {tokensHelpText ?? DEFAULT_TOKENS_HELP}
      </p>
    </div>
  );
}
